import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { debounce } from 'lodash';

import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress, IconButton, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { fetchRootList } from 'store/actions/contacts';
import { fetchSearchedContacts, resetSearchedContacts } from 'store/actions/crm';
import { contactSelectors } from 'store/selectors/contacts';
import { crmSelectors } from 'store/selectors/crm';
import { organizationSelectors } from 'store/selectors/organizations';
import { colors } from 'themes';
import { trackEvent } from 'utils/analytics';

import { SearchResultOption } from 'common/components';

const MIN_SEARCH_LENGTH = 3;

const SEARCH_CONTEXT = 'navigation';

const contactLabel = contact => `${contact.firstName || ''} ${contact.lastName || ''}`.trim();

const contactKey = contact => contact.contact || contact.uuid;

const contactAddress = contact => {
  const location = contact.locations?.[0];

  if (!location) {
    return '';
  }

  return [
    location.streetAddress,
    location.city,
    [location.state, location.zipCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

const SearchWrapper = styled.div`
  display: inline-flex;
  width: 240px;
  margin-left: 20px;
  vertical-align: middle;
`;

const SearchIconButton = styled(IconButton)`
  margin-left: 20px;
  color: ${colors.white};
  vertical-align: middle;
`;

const NavTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    color: ${colors.white};

    fieldset {
      border-color: ${colors.white};
    }

    &:hover fieldset,
    &.Mui-focused fieldset {
      border-color: ${colors.white};
    }
  }

  .MuiInputBase-input::placeholder {
    color: ${colors.white};
    opacity: 0.7;
  }

  .MuiSvgIcon-root {
    color: ${colors.white};
  }
`;

/**
 * Global contact search in the main navigation. Only rendered when the
 * organization uses lists (`showLists`) and the `showContactSearch` flag is
 * enabled. Renders as a search icon
 * that expands to a search field when clicked (collapsing again on blur).
 * Typing three or more characters triggers a debounced server-side search
 * across all of the organization's contacts; selecting a result navigates
 * to that contact's detail page under the organization's root list.
 */
const NavContactSearch = ({ organizationUuid }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isSearching = useSelector(crmSelectors.isSearching);
  const searchedContacts = useSelector(crmSelectors.getSearchedContacts);
  const rootListUuid = useSelector(
    state => contactSelectors.getOrganizationRootList(state)[organizationUuid]?.uuid
  );
  const flags = useSelector(state => organizationSelectors.getOrganization(state).flags);

  const isEnabled = !!flags?.showLists && !!flags?.showContactSearch;

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchedPhrase, setSearchedPhrase] = useState('');

  // Tracks whether `search_initiated` has fired for the current search, so
  // that refining a phrase reports one search rather than one per keystroke.
  const hasTrackedSearch = useRef(false);

  useEffect(() => {
    if (isEnabled && !rootListUuid) {
      dispatch(fetchRootList(organizationUuid));
    }
  }, [dispatch, isEnabled, organizationUuid, rootListUuid]);

  const debouncedFetchSearchedContacts = useCallback(
    debounce(phrase => {
      if (!hasTrackedSearch.current) {
        hasTrackedSearch.current = true;
        trackEvent('search_initiated', {
          search_context: SEARCH_CONTEXT,
          search_type: 'contact',
        });
      }

      dispatch(fetchSearchedContacts(phrase, organizationUuid));
    }, 500),
    [organizationUuid]
  );

  const collapseSearch = () => {
    debouncedFetchSearchedContacts.cancel();
    dispatch(resetSearchedContacts());
    hasTrackedSearch.current = false;
    setSearchedPhrase('');
    setIsExpanded(false);
  };

  if (!isEnabled) {
    return null;
  }

  if (!isExpanded) {
    return (
      <SearchIconButton
        aria-label="Search contacts"
        data-test-id="nav-contact-search-button"
        onClick={() => setIsExpanded(true)}
      >
        <SearchIcon />
      </SearchIconButton>
    );
  }

  return (
    <SearchWrapper>
      <Autocomplete
        fullWidth
        blurOnSelect
        getOptionLabel={contactLabel}
        // https://github.com/mui/material-ui/issues/20068
        filterOptions={x => x}
        inputValue={searchedPhrase}
        isOptionEqualToValue={(option, value) => contactKey(option) === contactKey(value)}
        loading={isSearching}
        noOptionsText={
          searchedPhrase.length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
            : 'No matching contacts'
        }
        onChange={(_, contact) => {
          if (!contact || !rootListUuid) {
            return;
          }

          trackEvent('search_result_selected', {
            query_length: searchedPhrase.length,
            result_count: searchedContacts.length,
            result_index: searchedContacts.findIndex(
              option => contactKey(option) === contactKey(contact)
            ),
            search_context: SEARCH_CONTEXT,
            search_type: 'contact',
          });

          collapseSearch();
          history.push(
            `/${organizationUuid}/contacts/lists/${rootListUuid}/contact/${contactKey(contact)}`
          );
        }}
        onInputChange={(_, newValue) => {
          if (newValue.length >= MIN_SEARCH_LENGTH) {
            debouncedFetchSearchedContacts(newValue);
          } else {
            debouncedFetchSearchedContacts.cancel();
            dispatch(resetSearchedContacts());
          }

          setSearchedPhrase(newValue);
        }}
        options={searchedContacts}
        renderOption={(props, option) => (
          <SearchResultOption
            key={contactKey(option)}
            meta={contactAddress(option)}
            name={contactLabel(option)}
            optionProps={props}
          />
        )}
        renderInput={params => (
          <NavTextField
            {...params}
            autoFocus
            onBlur={collapseSearch}
            placeholder="Search contacts"
            size="small"
            variant="outlined"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                startAdornment: <SearchIcon fontSize="small" />,
              },
            }}
          />
        )}
        value={null}
      />
    </SearchWrapper>
  );
};

NavContactSearch.propTypes = {
  organizationUuid: PropTypes.string.isRequired,
};

export default NavContactSearch;
